import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';

import { makeStyles } from 'tss-react/mui';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Grid,
  PAYLOAD_DATE_FORMAT,
  Progress,
  SectionHeader,
  stringToPayloadFormat,
  Typography,
} from 'web-library';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import dayjs from 'dayjs';
import { MutationStatus, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import PersonalDetails from '../particulars/PersonalDetails';
import ContactDetails from '../particulars/ContactDetails';
import NextOfKinContactDetails from '../particulars/NextOfKinContactDetails';
import { CandidateProfileFormData, CandidateProfileIsDisableInputs } from '../../../../models';
import { getPreferredAddress, getPreferredContactType } from '../../../../utils/ComponentUtils';
import {
  CandidateProfileDetailsResponse,
  IExamMasterCodeItem,
  LookupListApiItem,
  LookupListItem,
  UpdateCandidateProfileDetailsRequest,
} from '../../../../api/user/model';
import candidateProfileSPERSSchema from './candidateProfileSPERSSchema';
import {
  SubmitSPEAdditionalDetailsRequest,
  usePostSubmitSPEAdditionalDetails,
} from '../../../../api-refactored/non-national-exam-registration/usePostSubmitSPEAdditionalDetails';
import RouteKey from '../../../../routes/key';
import {
  ParentParticularsLocation
} from '../../../../pages/examRegistration/NonNationalExam/AEIS/ParentParticulars';
import {
  SPEAdditionalDetailsResponse,
  useGetSPEAdditionalDetails,
} from '../../../../api-refactored/non-national-exam-registration/useGetSPEAdditionalDetails';
import {
  IExamContentResponse,
  useGetIExamMasterCode,
} from '../../../../api-refactored/user/useGetIExamMasterCode';
import {
  useGetIntendedResidentialZone
} from '../../../../api-refactored/non-national-exam-registration/useGetIntendedResidentialZone';
import {
  useGetCountry
} from '../../../../api-refactored/non-national-exam-registration/useGetCountry';
import { GET_SPE_ADDITIONAL_DETAILS_QUERY_KEY } from '../../../../constants/queryKeys';
import {
  usePutCandidateProfileDetailsForOpsAdmin
} from '../../../../api-refactored/non-national-exam-registration/usePutCandidateProfileDetailsForOpsAdmin';
import {
  usePutCandidateProfileDetails
} from '../../../../api-refactored/user/usePutCandidateProfileDetails';
import { UseCustomMutationReturn } from '../../../../api-refactored/utils/useCustomMutation';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';
import { existInArray } from '../../../../utils/ArrayUtils';
import { GenericErrorResponse } from "../../../../api";

const { confirmAndPay, summary } = RouteKey.exam.child;

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  dialogBold: {
    fontWeight: 'bold',
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  button: {
    [theme.breakpoints.down('sm')]: {
      height: 'fit-content',
    },
  },
  buttonContainer: {
    marginBottom: theme.spacing(10),
  },
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
}));

const disabledInput = (isOpsAdmin: boolean): CandidateProfileIsDisableInputs => ({
  name: !isOpsAdmin,
  IdType: true,
  NricFin: !isOpsAdmin,
  passportNo: false,
  hanyupinyinName: true,
  gender: !isOpsAdmin,
  dateOfBirth: !isOpsAdmin,
  race: !isOpsAdmin,
  secondaryRace: true,
  nationality: !isOpsAdmin,
  prStatus: true,
  nationalityIssueDate: true,
  citizenship: true,
  occupation: true,
  school: true,
  houseOrBlkNo: false,
  streetName: false,
  postalCode: false,
  intendedResidentialZone: false,
  overseasResidenceCity: false,
  overseasResidenceState: false,
  overseasResidenceCountry: false,
  overseasResidencePeriodOfResidenceFrom: false,
  overseasResidencePeriodOfResidenceTo: false,
});

const updateCandidateDetails = (
  formData: CandidateProfileFormData,
  setApiButtonStatus: React.Dispatch<React.SetStateAction<MutationStatus>>,
  formDefaultValues: CandidateProfileFormData | undefined,
  updateCandidateProfileMutation: UseCustomMutationReturn<
    void,
    AxiosError,
    CandidateProfileDetailsResponse,
    unknown
  >,
  updateAdditionalDetailsMutation: UseCustomMutationReturn<
    void,
    AxiosError,
    SubmitSPEAdditionalDetailsRequest,
    unknown
  >,
  nextPage: () => void,
  lockedByUser?: string
) => {
  if (lockedByUser) {
    nextPage();
    return;
  }

  updateAdditionalDetailsMutation.reset();
  updateCandidateProfileMutation.reset();
  setApiButtonStatus('loading');
  const putRequestBody: UpdateCandidateProfileDetailsRequest = {
    statutoryName: formData.name,
    candidateId: formData.NricFin,
    userIdType: formDefaultValues?.IdType === 'I' ? 'NRIC_OR_FIN' : 'PASSPORT',
    passportNo: formDefaultValues?.passportNo,
    hanyuPinyinName: formData.hanyupinyinName,
    email: formData.email,
    citizenship: formDefaultValues?.citizenship,
    nationality: formData?.nationality,
    dateOfBirth: dayjs(
      formData.dateOfBirth ? formData.dateOfBirth : formDefaultValues?.dateOfBirth
    ).format(PAYLOAD_DATE_FORMAT),
    prStatus: formDefaultValues?.prStatus,
    gender: formData?.gender,
    race: formData?.race,
    occupation: null,
    // It seems that school field details won't be affected no matter what non-empty code or description is defined.
    school: { code: '1', description: '2' },

    // addresses
    residentialAddress: {
      houseOrBlockNumber: formData.houseOrBlkNo,
      floorNumber: formData.floor,
      unitNumber: formData.unit,
      buildingName: formData.building,
      streetName: formData.streetName,
      postalCode: formData.postalCode,
    },
    foreignAddress1: formData.foreignAddress1,
    foreignAddress2: formData.foreignAddress2,
    foreignAddress3: formData.foreignAddress3,
    localAddressPreferred: formData.preferredAddress === 'L',

    // contact numbers
    mobileNumber: {
      countryCode: formData.mobileCountryCode,
      number: formData.mobileContactNumber,
    },
    telephoneNumber: {
      countryCode: formData.telephoneCountryCode,
      number: formData.telephoneContactNumber,
    },
    mobileNumberPreferred: formData.preferredTelephoneOrMobile === 'M',

    // next of kin
    nextOfKinName: formData.nextOfKinName,
    nextOfKinContactNumber: {
      countryCode: formData.nextOfKinCountryCode,
      number: formData.nextOfKinContactNumber,
    },
    nextOfKinRelationship: formData.nextOfKinRelationshipToCandidate,
  };
  updateCandidateProfileMutation.mutate(putRequestBody);

  const SPEAdditionalDetailsRequestBody = {
    intendedResidentialZone: formData?.intendedResidentialZone?.code || '',
    overseasResidenceCity: formData?.overseasResidenceCity || '',
    overseasResidenceCountry: formData?.overseasResidenceCountry?.code || '',
    overseasResidenceState: formData?.overseasResidenceState || '',
    overseasResidencePeriodOfResidenceFrom:
      stringToPayloadFormat(formData?.overseasResidencePeriodOfResidenceFrom as string) || '',
    overseasResidencePeriodOfResidenceTo:
      stringToPayloadFormat(formData?.overseasResidencePeriodOfResidenceTo as string) || '',
    passportNo: formData.passportNo || '',
    nricFinPassport: formData.NricFin || '',
  };
  updateAdditionalDetailsMutation.mutate(SPEAdditionalDetailsRequestBody);
};

const getFormDefaultValuesObject = (
  profileData: SPEAdditionalDetailsResponse,
  iExamsData: IExamContentResponse,
  residentialZones: LookupListApiItem[],
  countryData: LookupListApiItem[]
): CandidateProfileFormData => ({
  name: profileData.name,
  hanyupinyinName: profileData.hanyuPinyinName,
  IdType: profileData.userIdType === 'NRIC/FIN' ? 'I' : 'P',
  NricFin: profileData.nricFinPassport,
  dateOfBirth: profileData.dob,
  email: profileData.email,
  passportNo: profileData.passportNo || '',
  gender: iExamsData?.gender.find(
    ({ codeValueDescription }) => codeValueDescription === profileData.gender.toUpperCase()
  ) as IExamMasterCodeItem,
  race: iExamsData?.race.find(
    ({ codeValueDescription }) => codeValueDescription === profileData.race.toUpperCase()
  ) as IExamMasterCodeItem,
  citizenship: iExamsData?.citizenship.find(
    ({ codeValueDescription }) => codeValueDescription === profileData.citizenship
  ) as IExamMasterCodeItem,
  nationality: iExamsData?.nationality.find(
    ({ codeValueDescription }) => codeValueDescription === profileData.nationality
  ) as IExamMasterCodeItem,
  nationalityIssueDate: profileData.nationalityIssueDate,
  prStatus: iExamsData?.prStatus.find(
    ({ codeValueDescription }) => codeValueDescription === profileData.prStatus
  ) as IExamMasterCodeItem,
  // addresses
  houseOrBlkNo: profileData.mailingAddress?.houseOrBlockNumber,
  streetName: profileData.mailingAddress?.streetName,
  building: profileData.mailingAddress?.buildingName,
  floor: profileData.mailingAddress?.floorNumber,
  unit: profileData.mailingAddress?.unitNumber,
  postalCode: profileData.mailingAddress?.postalCode,
  foreignAddress1: profileData.foreignAddress1,
  foreignAddress2: profileData.foreignAddress2,
  foreignAddress3: profileData.foreignAddress3,
  preferredAddress: getPreferredAddress(profileData.localAddressPreferred),
  school: { id: 0, name: profileData.schoolName } as LookupListItem,

  // contact numbers
  telephoneCountryCode: (profileData.phoneNumber?.countryCode as IExamMasterCodeItem) ?? null,
  telephoneContactNumber: profileData.phoneNumber?.number ?? null,
  mobileCountryCode: profileData.mobileNumber?.countryCode as IExamMasterCodeItem,
  mobileContactNumber: profileData.mobileNumber?.number,
  preferredTelephoneOrMobile: getPreferredContactType(profileData.mobileNumberPreferred),

  // next of kin
  nextOfKinName: profileData.nextOfKinName,
  nextOfKinCountryCode: profileData.nextOfKin?.countryCode as IExamMasterCodeItem,
  nextOfKinContactNumber: profileData.nextOfKin?.number,
  nextOfKinRelationshipToCandidate: iExamsData?.relationship.find(
    ({ codeValueDescription }) => codeValueDescription === profileData.nextOfKinRelationship
  ) as IExamMasterCodeItem,

  // additional SPE details
  intendedResidentialZone: residentialZones?.find(
    ({ code }) => code === profileData.intendedResidentialZone
  ) as LookupListApiItem || null,
  overseasResidenceCity: profileData.overseasResidenceCity || '',
  overseasResidenceCountry: countryData?.find(
    ({ description }) => description === profileData.overseasResidenceCountry
  ) as LookupListApiItem,
  overseasResidenceState: profileData.overseasResidenceState || '',
  overseasResidencePeriodOfResidenceFrom: profileData.overseasResidencePeriodOfResidenceFrom || '',
  overseasResidencePeriodOfResidenceTo: profileData.overseasResidencePeriodOfResidenceTo || '',
});

const onErrorHandler = (
  err: AxiosError<GenericErrorResponse>,
  t: TFunction,
  setErrorMessage: Dispatch<SetStateAction<string>>,
  setApiButtonStatus: Dispatch<SetStateAction<MutationStatus>>
) => {
  const serverMessage = err?.response?.data?.message;
  if (serverMessage) {
    setErrorMessage(serverMessage);
  } else {
    setErrorMessage(t('components->>Error.DefaultErrorMsg'));
  }
  setApiButtonStatus('error');
};

const ConfirmParticularsSPERS = ({ isOpsAdmin }: { isOpsAdmin: boolean }) => {
  const { classes } = useStyles();
  const { uuid: applicationUuid } = useParams<{ uuid: string }>();
  const { state } = useLocation<ParentParticularsLocation>();

  const { t } = useTranslation();
  const history = useHistory();
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [isSettingUpFormValue, setIsSettingUpFormValue] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const queryClient = useQueryClient();
  const authorities = useGetUserAuthorities();
  const hasViewAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_VIEW)
    : true;
  const hasEditAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_EDIT)
    : true;
  const onBack = () => {
    history.goBack();
  };

  const { data: profileData, isFetching: isLoadingProfileData } = useGetSPEAdditionalDetails({
    uuid: applicationUuid,
  });
  const { data: iExamsData } = useGetIExamMasterCode();
  const { data: residentialZones } = useGetIntendedResidentialZone();
  const { data: countryData } = useGetCountry({ isSpers: true });

  const formDefaultValues: CandidateProfileFormData | undefined = useMemo(() => {
    if (!profileData || !residentialZones || !iExamsData || !countryData) {
      return undefined;
    }
    return getFormDefaultValuesObject(profileData, iExamsData, residentialZones, countryData);
  }, [profileData, residentialZones, iExamsData, countryData]);

  useEffect(() => {
    if (formDefaultValues) {
      form.reset(formDefaultValues);
      setIsSettingUpFormValue(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDefaultValues]);

  const form = useForm<CandidateProfileFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(
      candidateProfileSPERSSchema(
        disabledInput(isOpsAdmin),
        'Next-of-kin Name',
        'Next-of-kin Contact No.'
      )
    ),
    defaultValues: formDefaultValues,
  });

  const updateCandidateProfileMutation = usePutCandidateProfileDetails({
    onError: (err) => onErrorHandler(err, t, setErrorMessage, setApiButtonStatus),
  });
  const opsAdminUpdateProfileMutation = usePutCandidateProfileDetailsForOpsAdmin({
    uuid: applicationUuid,
    onError: (err) => onErrorHandler(err, t, setErrorMessage, setApiButtonStatus),
  });
  const updateAdditionalDetailsMutation = usePostSubmitSPEAdditionalDetails({
    uuid: applicationUuid,
    onError: (err) => onErrorHandler(err, t, setErrorMessage, setApiButtonStatus),
  });

  useEffect(() => {
    if (
      (isOpsAdmin
        ? opsAdminUpdateProfileMutation.status === 'success'
        : updateCandidateProfileMutation.status === 'success') &&
      updateAdditionalDetailsMutation.status === 'success'
    ) {
      queryClient.invalidateQueries(GET_SPE_ADDITIONAL_DETAILS_QUERY_KEY);
      nextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    updateCandidateProfileMutation.status,
    opsAdminUpdateProfileMutation.status,
    updateAdditionalDetailsMutation.status,
  ]);

  const nextPage = () =>
    history.push(isOpsAdmin ? `${summary.route}` : `${confirmAndPay.route}`, state);

  return (
    <>
      {isSettingUpFormValue || isLoadingProfileData || !profileData ? (
        <ContentTemplate>
          <Progress color="secondary" />
        </ContentTemplate>
      ) : (
        <Box>
          <form
            onSubmit={form.handleSubmit(
              (formData) => {
                setErrorMessage('');
                updateCandidateDetails(
                  formData,
                  setApiButtonStatus,
                  formDefaultValues,
                  isOpsAdmin ? opsAdminUpdateProfileMutation : updateCandidateProfileMutation,
                  updateAdditionalDetailsMutation,
                  nextPage,
                  profileData?.lockedByUser
                );
              },
              () => {
                if (hasViewAccess && !hasEditAccess) {
                  nextPage();
                }
              }
            )}
          >
            {formDefaultValues && (
              <ContentTemplate isContinuable type="full">
                <SectionHeader title={t('exam->>NonNationalExam.ConfirmParticulars')} />
                <PersonalDetails
                  sameRowName
                  examCandidateDetail={profileData}
                  isEditState={hasEditAccess}
                  isSPERS
                  form={form}
                  isOpsAdmin={isOpsAdmin}
                  defaultExpanded
                />
                <ContactDetails
                  contactDetails={profileData}
                  formDefaultValues={formDefaultValues}
                  sameRowAddress
                  sameRowPhone
                  isEditState={hasEditAccess}
                  isSPERS
                  form={form}
                  defaultExpanded
                />
                <NextOfKinContactDetails
                  nextOfKinName={profileData?.nextOfKinName}
                  form={form}
                  formDefaultValues={formDefaultValues}
                  accordionBottomLine={false}
                  isEditState={hasEditAccess}
                  isLastItem
                  nextOfKinRelationship={profileData?.nextOfKinRelationship}
                  defaultExpanded
                />
              </ContentTemplate>
            )}
            <ContentTemplate continuous>
              {errorMessage && (
                <Typography variant="body2" className={classes.errString}>
                  {errorMessage}
                </Typography>
              )}
              <Box className={classes.buttonContainer}>
                {profileData?.lockedByUser && (
                  <Typography variant="body2" className={classes.errString}>
                    {t('exam->>NonNationalExam.LockedMessage', {
                      lockedByUser: profileData?.lockedByUser,
                    })}
                  </Typography>
                )}
                <Grid container direction="row" alignItems="flex-start" spacing={3}>
                  <Grid xs={12} md={3}>
                    <Button
                      id="button-confirmParticulars-back"
                      fullWidth
                      className={classes.button}
                      variant="outlined"
                      onClick={onBack}
                    >
                      {t('exam->>ReviewExam.Back')}
                    </Button>
                  </Grid>
                  <Grid xs={12} md={4}>
                    <ApiButton
                      id="button-confirmParticulars-confirm"
                      fullWidth
                      type="submit"
                      status={apiButtonStatus}
                      className={classes.button}
                      variant="contained"
                      disabled={apiButtonStatus === 'loading'}
                    >
                      {t('exam->>ReviewExam.Confirm Particulars')}
                    </ApiButton>
                  </Grid>
                </Grid>
              </Box>
            </ContentTemplate>
          </form>
        </Box>
      )}
    </>
  );
};

export default ConfirmParticularsSPERS;
